import React, { FC, useState } from 'react';
import { Button, Input, Select, Tooltip } from 'antd';
import { Divider } from 'common/components';
import { ArrowDownShort, Bin, Close, Plus } from 'common/svg';
import { getId } from 'common/helpers';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { ICreateUserAssigneePayload } from 'entities/User/User.models';
import { getUserAssigneeOptions, getUserKey } from 'entities/User/User.helper';
import { CreateUserAssigneeModal, DeleteUserAssigneeModal } from 'entities/User/components';
import {
  createUserAssignee,
  deleteUserAssignee,
  selectIsUserLoading,
  selectUserAssignees,
  selectUserList,
} from 'entities/User/User.slice';

interface IAntdFormControl {
  onChange?: (value?: any) => void;
  value?: any;
}

export const UserAssigneeSelector: FC<IAntdFormControl> = ({ value, onChange }) => {
  const dispatch = useAppDispatch();
  // user
  const userList = useAppSelector(selectUserList);
  const isUserLoading = useAppSelector(selectIsUserLoading);
  const userAssignees = useAppSelector(selectUserAssignees);

  const [search, setSearch] = useState<string>('');
  const [selectedAssigneeKey, setSelectedAssigneeKey] = useState<string | null>(null);
  const [deletingAssigneeKey, setDeletingAssigneeKey] = useState<string | null>(null);
  const [openUserAssigneeSelector, setOpenUserAssigneeSelector] = useState<boolean>(false);
  const [openCreateUserAssigneeModal, setOpenCreateUserAssigneeModal] = useState<boolean>(false);
  const [openDeleteUserAssigneeModal, setOpenDeleteUserAssigneeModal] = useState<boolean>(false);

  const options = getUserAssigneeOptions(userList, userAssignees, search);

  const toggleOpenAssigneeSelector = (open: boolean): void => {
    setSearch('');
    setOpenUserAssigneeSelector(open);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
  };

  const handleAssigneeChange = (value: string | null) => {
    setSelectedAssigneeKey(value);
    onChange?.(value);
  };

  const openCreateAssigneeModal = (): void => {
    setOpenCreateUserAssigneeModal(true);
  };

  const closeCreateAssigneeModal = (): void => {
    setOpenUserAssigneeSelector(true);
    setOpenCreateUserAssigneeModal(false);
  };

  const handleCreateUserAssignee = async (payload: ICreateUserAssigneePayload): Promise<void> => {
    const assignee = await dispatch(createUserAssignee(payload));

    if (!assignee) {
      return;
    }

    const key = getUserKey(assignee.id, assignee.name);

    setSelectedAssigneeKey(key);
    onChange?.(key);
  };

  const openDeleteAssigneeModal = (e: React.MouseEvent, value: string): void => {
    e.stopPropagation();
    setDeletingAssigneeKey(value);
    setOpenDeleteUserAssigneeModal(true);
  };

  const closeDeleteAssigneeModal = (): void => {
    setDeletingAssigneeKey(null);
    setOpenUserAssigneeSelector(true);
    setOpenDeleteUserAssigneeModal(false);
  };

  const handleDeleteUserAssignee = (): void => {
    if (!deletingAssigneeKey) {
      return;
    }

    const id = Number(deletingAssigneeKey.split('_')[0]);

    dispatch(
      deleteUserAssignee({
        id,
        onSuccess: () => {
          if (deletingAssigneeKey === selectedAssigneeKey) {
            setSelectedAssigneeKey(null);
            onChange?.(null);
          }

          setOpenUserAssigneeSelector(true);
          setOpenDeleteUserAssigneeModal(false);
          setDeletingAssigneeKey(null);
        },
      }),
    );
  };

  return (
    <div>
      <Select
        open={openUserAssigneeSelector || openCreateUserAssigneeModal || openDeleteUserAssigneeModal}
        onDropdownVisibleChange={toggleOpenAssigneeSelector}
        style={{ width: 315 }}
        dropdownStyle={{ zIndex: 1001, width: 636 }}
        rootClassName="redesign"
        placeholder="Укажите ФИО лица"
        virtual={false}
        suffixIcon={<ArrowDownShort />}
        notFoundContent="В списке нет вариантов."
        optionLabelProp="label"
        value={value}
        onChange={handleAssigneeChange}
        popupMatchSelectWidth={false}
        dropdownRender={(menu) => {
          return (
            <>
              <div className="mb-6">
                <Input
                  allowClear={{ clearIcon: <Close className="icon-close-input" /> }}
                  value={search}
                  onChange={handleSearch}
                />
              </div>

              <Button
                className="button-text dark-green"
                icon={<Plus className="icon-plus-dark-green" />}
                onClick={openCreateAssigneeModal}
              >
                Добавить сотрудника
              </Button>

              <Divider marginY={20} />

              {menu}
            </>
          );
        }}
      >
        {options.map((item) => {
          const selected = item.value === selectedAssigneeKey;

          if (item.type === 'divider') {
            return <Select.Option key={getId()} type="divider" children={null} />;
          }

          return (
            <Select.Option key={item.value} value={item.value} label={item.label}>
              <div className="flex align-center justify-between">
                <span className={`${selected ? 'ff-medium' : 'ff-regular'} fs-m text-ellipsis`}>{item.label}</span>

                {item.extra && (
                  <Tooltip title="Удалить" placement="bottom">
                    <Button
                      className="button-icon"
                      icon={<Bin className="icon-pale-red" />}
                      onClick={(e) => openDeleteAssigneeModal(e, item.value as string)}
                    />
                  </Tooltip>
                )}
              </div>
            </Select.Option>
          );
        })}
      </Select>

      <CreateUserAssigneeModal
        open={openCreateUserAssigneeModal}
        loading={isUserLoading}
        onCancel={closeCreateAssigneeModal}
        onSubmit={handleCreateUserAssignee}
      />

      <DeleteUserAssigneeModal
        open={openDeleteUserAssigneeModal}
        loading={isUserLoading}
        onCancel={closeDeleteAssigneeModal}
        onSubmit={handleDeleteUserAssignee}
      />
    </div>
  );
};
